import { useContext, useEffect, useState } from "react";
import AuthContext from "../Context/AuthProvider";
import {
  addPrescription,
  getPrescription,
  removePrescription,
  updatePrescription,
} from "../API/PrescriptionApi";
import { getServiceDetails } from "../API/LinkedServicesApi";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  FormControl,
  FormLabel,
  Grid2,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "../Utility/UserMessageContext";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import {
  PhysioTreatmentMethods,
  PrescriptionTypes,
  SelectableMedicalHistoryConditions,
  SelectablePainType,
  painLevelValue,
} from "../Utility/Constants";
import NotesTextField from "../Worklist/NotesTextField";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import PhysiotherapyPrescriptionReadOnly from "./PhysiotherapyPrescriptionReadOnly";
import { formatAge, showLoadingImage } from "../Utility/Utility";
import MenuBar from "../common/MenuBar";
import "../common/styles/CommonStyles.css"; // Import your custom styles
import GenericTextField from "../common/GenericTextField";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import { clearFormDataFromLocal, getFormDataFromLocal, saveFormDataToLocal } from "../Utility/FormDataTempStorage";
import { getPhysiotherapyPrescriptionDataJson } from "./PhysiotherapyPrescriptionDataStructure";

function PhysiotherapyPrescriptionForm({
  serviceId,
  uniqueId,
  prescriptionId,
  changeTabsLabel,
  onRemovePrescription,
  onSavePrescription,
}) {
  const { auth } = useContext(AuthContext);
  const [detailedPrescription, setDetailedPrescription] = useState(getPhysiotherapyPrescriptionDataJson());
  const [prescriptionStatus, setPrescriptionStatus] = useState();
  const [currentService, setCurrentService] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prescriptionToBeEdited, setPrescriptionToBeEdited] = useState();
  const [currentPrescriptionId, setCurrentPrescriptionId] =
    useState(prescriptionId);
  const [readOnly, setReadOnly] = useState(false);
  const showLoading = isLoading || showSpinner;

  const { showSnackbar } = useSnackbar();
  const displayText = (value, fallback = "Not available") => {
    return value !== undefined && value !== null ? value : fallback;
  };

  useEffect(() => {
    const loadService = async () => {
      if (auth != null && serviceId != null) {
        setIsSaving(true); // Set loading state when fetching service details
        try {
          var result = await getServiceDetails(auth, serviceId);
          if (result?.isSuccess) {
            setCurrentService(result?.data);
          } else {
            showSnackbar(result?.data, "error");
          }
        } catch (error) {
          showSnackbar(
            "Error loading service details: " + error.message,
            "error",
          );
        } finally {
          // Only set loading to false if we're not also loading the prescription
          if (currentPrescriptionId == null) {
            setIsSaving(false);
          }
        }
      }
    };

    const loadPrescription = async () => {
      const unsavedData = getFormDataFromLocal(uniqueId);
      if (unsavedData) {
        setDetailedPrescription(unsavedData);
      } else if (auth != null && currentPrescriptionId != null) {
        setIsSaving(true);
        try {
          var result = await getPrescription(auth, currentPrescriptionId);
          if (result.isSuccess) {
            var prescription = result?.data;
            if (changeTabsLabel) {
              changeTabsLabel(uniqueId, `${prescription?.displayId}`); // Update tab label with prescription display ID
            }
            setPrescriptionToBeEdited(prescription);
            setPrescriptionStatus(prescription?.status);

            // If status is "Final", set readOnly to true
            if (prescription?.status === "Final") {
              setReadOnly(true);
            }

            var detailedPrescriptionJson = JSON.parse(
              prescription?.detailedPrescription,
            );

            if (prescription?.detailedPrescription) {
              setDetailedPrescription(detailedPrescriptionJson);
            } else {
              setDetailedPrescription(getPhysiotherapyPrescriptionDataJson());
            }
          } else {
            showSnackbar(result?.data, "error");
          }
        } catch (error) {
          showSnackbar("Error loading prescription: " + error.message, "error");
        } finally {
          setIsSaving(false);
        }
      } else {
        setDetailedPrescription(getPhysiotherapyPrescriptionDataJson());
      }
    };
    loadPrescription();
    loadService();
    setIsSaving(false);
  }, [auth, serviceId, currentPrescriptionId]);

  const savePrescription = async (status) => {
    setIsSaving(true);
    var result = {};

    try {
      if (!currentPrescriptionId) {
        result = await addPrescription(
          auth,
          detailedPrescription,
          currentService?.id,
          PrescriptionTypes.PhysiotherapyPrescription,
          status,
        );
        setCurrentPrescriptionId(result?.data);
      } else {
        result = await updatePrescription(
          auth,
          detailedPrescription,
          currentPrescriptionId,
          status,
        );
      }

      if (result.isSuccess) {
        //clearFormData(currentPrescriptionId); 
        clearFormDataFromLocal(uniqueId); 
        // If status is set to "Final", update readOnly state
        if (status === "Final") {
          setReadOnly(true);
        }

        onSavePrescription(
          serviceId,
          currentPrescriptionId || result?.data,
          status,
        );
        showSnackbar(
          "The prescription has been saved successfully.",
          "success",
        );
      } else {
        showSnackbar(result?.data, "error");
      }
    } catch (error) {
      showSnackbar("Error saving prescription: " + error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const onRemovePrescriptionClick = async () => {
    if (auth != null && currentPrescriptionId != null) {
      setIsSaving(true);
      try {
        var result = await removePrescription(auth, currentPrescriptionId);
        if (result?.isSuccess) {
          onRemovePrescription(serviceId, currentPrescriptionId);
        } else {
          showSnackbar(result?.data, "error");
        }
      } catch (error) {
        showSnackbar("Error removing prescription: " + error.message, "error");
      } finally {
        setIsSaving(false);
      }
    }
  };

  const [othersChecked, setOthersChecked] = useState();

  const handleOthersCheck = (newConditions) => {
    const othersIndex = newConditions.findIndex(
      (condition) => condition.value === "Others",
    );
    if (othersIndex !== -1) {
      setOthersChecked(newConditions[othersIndex].selected);
    }
  };

  const handlePrescriptionDetailsChange = (updatedValues = {}) => {
    const updatedDetailedPrescription = {...detailedPrescription, ...updatedValues};
    saveFormDataToLocal(currentPrescriptionId ? currentPrescriptionId : uniqueId, updatedDetailedPrescription);
  }

  // If readOnly or status is "Final", render the ReadOnly component
  if (readOnly || prescriptionStatus === "Final") {
    return (
      <PhysiotherapyPrescriptionReadOnly
        serviceId={serviceId}
        prescriptionId={currentPrescriptionId}
      />
    );
  }

  let clinicalServiceCode = displayText(currentService?.serviceCode);
  let serviceName = displayText(currentService?.serviceName);

  let finalserviceName = clinicalServiceCode +" "+ serviceName;

  // Editable form view
  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Loading Overlay using only isSaving state */}

      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301, // One level above Dialog
          }}
        >
          {showLoadingImage()}
        </div>
      )}

      <Grid2 container size={12}>
        <Grid2 container size={12}>
          <Grid2 size={12}>
            <MenuBar
              showSave={true}
              onSave={() => savePrescription("Draft")}
              //saveDisabled={isPrescriptionEmpty()} // ADD THIS LINE
              showFinalize={true}
              onFinalize={() => savePrescription("Final")}
              //finalizeDisabled={isPrescriptionEmpty()} // ADD THIS LINE
              showDelete={prescriptionStatus === "Draft"}
              onDelete={onRemovePrescriptionClick}
              disabled={isSaving}
            />
          </Grid2>
        </Grid2>

        <Grid2 container size={12}>
          <Grid2
            size={{ sx: 12, sm: 12, md: 12, lg: 9, xl: 9 }}
            sx={{ paddingTop: "10px" }}
          >
            <Card
              className="outerCard"
              variant="outlined"
              sx={{
                width: "100%",
                overflow: "auto",
                ml: "10px",
                padding: "10px",
              }}
            >
              <Grid2
                container
                spacing={1}
                size={12}
                sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
              >
                <Grid2 size={12}>
                  <Card
                    sx={{
                      padding: "16px",
                      pointerEvents: showLoading ? "none" : "auto",
                      opacity: showLoading ? 0.6 : 1,
                    }}
                    variant="outlined"
                  >
                    <Grid2 size={12}>
                      <Grid2 container size={12}>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <HeaderValueDisplay
                            header="Patient Name"
                            value={
                              currentService?.patientPrefix +
                              " " +
                              currentService?.patientFirstName +
                              " " +
                              currentService?.patientLastName
                            }
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <HeaderValueDisplay
                            header="Age and Gender"
                            value={
                              formatAge(currentService?.patientAgeInYears) +
                              " and " +
                              currentService?.patientGender
                            }
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <HeaderValueDisplay
                            header="MRN"
                            value={displayText(currentService?.patientMRN)}
                          />
                        </Grid2>
                      </Grid2>
                      <Grid2 container size={12}>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <HeaderValueDisplay
                            header="Visit Id"
                            value={displayText(currentService?.visitDisplayId)}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <HeaderValueDisplay
                            header="Visit Type"
                            value={displayText(currentService?.visitType)}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <HeaderValueDisplay
                            header="Visit Date Time"
                            value={convertUTCToTimeZoneDateTimeString(
                              new Date().toISOString(),
                              Intl.DateTimeFormat().resolvedOptions().timeZone,
                            )}
                          />
                        </Grid2>
                      </Grid2>
                      <Grid2 container size={12}>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <HeaderValueDisplay
                            header="Service Id"
                            value={displayText(currentService?.displayId)}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <HeaderValueDisplay
                            header="Service Name"
                            value={finalserviceName}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <HeaderValueDisplay
                            header="Service Date Time"
                            value={convertUTCToTimeZoneDateTimeString(
                              new Date().toISOString(),
                              Intl.DateTimeFormat().resolvedOptions().timeZone,
                            )}
                          />
                        </Grid2>
                      </Grid2>
                    </Grid2>
                  </Card>
                </Grid2>

                <Accordion
                  defaultExpanded
                  variant="outlined"
                  sx={{ width: "100%", maxWidth: "100%" }}
                >
                  <AccordionSummary
                    expandIcon={<GridExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <Typography component="span">
                      Complaint and Medical History
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ padding: "8px", position: "relative" }}
                  >
                    <Grid2
                      container
                      spacing={3}
                      size={12}
                      sx={{ justifyContent: "center", paddingX: "10px" }}
                    >
                      <Grid2 size={12}>
                        <Box sx={{ position: "relative" }}>
                          <NotesTextField
                            value={detailedPrescription?.complaint || ""}
                            onChange={(newValue) => {
                              setDetailedPrescription((prev) => ({
                                ...prev,
                                complaint: newValue,
                              }));
                              handlePrescriptionDetailsChange({ complaint: newValue });
                            }}
                            label="Chief Complaint"
                            disabled={
                              isSaving || prescriptionStatus === "Final"
                            }
                            rows={4}
                            noteStatus={prescriptionStatus || "Draft"}
                            tagsForAutocomplete={["physiotherapy-complaints"]}
                          />
                        </Box>
                      </Grid2>
                      <Grid2 size={12}>
                        <FormControl>
                          <FormLabel component="legend">
                            Medical History (Select applicable conditions)
                          </FormLabel>
                          <FormGroup row>
                            {detailedPrescription?.medicalHistoryConditions?.map(
                              (condition, index) => (
                                <FormControlLabel
                                  key={index}
                                  control={
                                    <Checkbox
                                      checked={condition.selected}
                                      onChange={(e) => {
                                        const newConditions = [
                                          ...detailedPrescription.medicalHistoryConditions,
                                        ];
                                        newConditions[index].selected =
                                          e.target.checked;
                                        setDetailedPrescription((prev) => ({
                                          ...prev,
                                          medicalHistoryConditions: newConditions,
                                        }));
                                        handlePrescriptionDetailsChange({ medicalHistoryConditions: newConditions });
                                      }}
                                    />
                                  }
                                  label={condition.displayValue}
                                />
                              ),
                            )}
                          </FormGroup>
                        </FormControl>
                      </Grid2>
                      <Grid2 size={12}>
                        <Box sx={{ position: "relative" }}>
                          <NotesTextField
                            value={detailedPrescription?.medicalHistoryNotes || ""}
                            onChange={(newValue) => {
                              setDetailedPrescription((prev) => ({
                                ...prev,
                                medicalHistoryNotes: newValue,
                              }));
                              handlePrescriptionDetailsChange({ medicalHistoryNotes: newValue });
                            }}
                            label="Medical History Notes"
                            disabled={
                              isSaving || prescriptionStatus === "Final"
                            }
                            rows={4}
                            noteStatus={prescriptionStatus || "Draft"}
                          />
                        </Box>
                      </Grid2>
                      <Grid2 size={12}>
                        <NotesTextField
                          value={detailedPrescription?.surgeryDetails || ""}
                          onChange={(newValue) => {
                            setDetailedPrescription((prev) => ({
                              ...prev,
                              surgeryDetails: newValue,
                            }));
                            handlePrescriptionDetailsChange({ surgeryDetails: newValue });
                          }}
                          label="Surgery Details"
                          multiline
                          rows={4}
                          // value={surgeryDetails}
                          // onChange={(e) => setSurgeryDetails(e.target.value)}
                          placeholder="Surgery Details"
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  defaultExpanded
                  variant="outlined"
                  sx={{ width: "100%", maxWidth: "100%" }}
                >
                  <AccordionSummary
                    expandIcon={<GridExpandMoreIcon />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                  >
                    <Typography component="span">Assessment</Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ padding: "8px", position: "relative" }}
                  >
                    <Grid2
                      container
                      spacing={3}
                      size={12}
                      sx={{ justifyContent: "center", paddingX: "10px" }}
                    >
                      <Grid2 size={12}>
                        <Typography>Pain Level (VAS)</Typography>
                      </Grid2>
                      <Grid2 container size={10}>
                        <Slider
                          value={Number(detailedPrescription?.painLevel) || 0}
                          marks={painLevelValue}
                          step={1}
                          min={0}
                          max={10}
                          valueLabelDisplay="auto"
                          onChange={(e) => {
                            setDetailedPrescription((prev) => ({
                              ...prev,
                              painLevel: Number(e.target.value),
                            }));
                            handlePrescriptionDetailsChange({ painLevel: Number(e.target.value) });
                          }}
                        />
                      </Grid2>

                      <Grid2 size={12}>
                        <Box sx={{ position: "relative" }}>
                          <NotesTextField
                            value={detailedPrescription?.painLevelNotes || ""}
                            onChange={(newValue) => {
                              setDetailedPrescription((prev) => ({
                                ...prev,
                                painLevelNotes: newValue,
                              }));
                              handlePrescriptionDetailsChange({ painLevelNotes: newValue });
                            }}
                            label="Pain Level Notes"
                            disabled={
                              isSaving || prescriptionStatus === "Final"
                            }
                            rows={4}
                            noteStatus={prescriptionStatus || "Draft"}
                          />
                        </Box>
                      </Grid2>

                      <Grid2 size={12}>
                        <FormControl>
                          <FormLabel component="legend">
                            Select applicable pain types
                          </FormLabel>
                          <FormGroup row>
                            {detailedPrescription?.painTypes?.map((painType, index) => (
                              <FormControlLabel
                                key={index}
                                control={
                                  <Checkbox
                                    checked={painType.selected}
                                    onChange={(e) => {
                                      const newConditions = [...detailedPrescription.painTypes];
                                      newConditions[index].selected =
                                        e.target.checked;
                                      setDetailedPrescription((prev) => ({
                                        ...prev,
                                        painTypes: newConditions,
                                      }));
                                      handlePrescriptionDetailsChange({ painTypes: newConditions });
                                    }}
                                  />
                                }
                                label={painType.displayValue}
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Grid2>
                      <Grid2 size={12}>
                        <Box sx={{ position: "relative" }}>
                          <NotesTextField
                            value={detailedPrescription?.painTypeNotes || ""}
                            onChange={(newValue) => {
                              setDetailedPrescription((prev) => ({
                                ...prev,
                                painTypeNotes: newValue,
                              }));
                              handlePrescriptionDetailsChange({ painTypeNotes: newValue });
                            }}
                            label="Pain Type Notes"
                            disabled={
                              isSaving || prescriptionStatus === "Final"
                            }
                            rows={4}
                            noteStatus={prescriptionStatus || "Draft"}
                          />
                        </Box>
                      </Grid2>
                      <Grid2 size={12}>
                        <NotesTextField
                          value={detailedPrescription?.rangeOfMotion || ""}
                          onChange={(newValue) => {
                            setDetailedPrescription((prev) => ({
                              ...prev,
                              rangeOfMotion: newValue,
                            }));
                            handlePrescriptionDetailsChange({ rangeOfMotion: newValue });
                          }}
                          label="Range of Motion"
                          multiline
                          rows={3}
                          // value={rangeOfMotion}
                          // onChange={(e) => setRangeOfMotion(e.target.value)}
                          placeholder="Enter range of motion details..."
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <NotesTextField
                          value={detailedPrescription?.muscleStrength || ""}
                          onChange={(newValue) => {
                            setDetailedPrescription((prev) => ({
                              ...prev,
                              muscleStrength: newValue,
                            }));
                            handlePrescriptionDetailsChange({ muscleStrength: newValue });
                          }}
                          label="Muscle Strength"
                          multiline
                          rows={3}
                          // value={muscleStrength}
                          // onChange={(e) => setMuscleStrength(e.target.value)}
                          placeholder="Enter muscle strength details..."
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <NotesTextField
                          value={detailedPrescription?.muscleTightness || ""}
                          onChange={(newValue) => {
                            setDetailedPrescription((prev) => ({
                              ...prev,
                              muscleTightness: newValue,
                            }));
                            handlePrescriptionDetailsChange({ muscleTightness: newValue });
                          }}
                          label="Muscle spasm \ Tightness \ Trigger points"
                          multiline
                          rows={3}
                          // value={muscleTightness}
                          // onChange={(e) => setMuscleTightness(e.target.value)}
                          placeholder="Enter muscle tightness details..."
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <NotesTextField
                          value={detailedPrescription?.specialTests || ""}
                          onChange={(newValue) => {
                            setDetailedPrescription((prev) => ({
                              ...prev,
                              specialTests: newValue,
                            }));
                            handlePrescriptionDetailsChange({ specialTests: newValue });
                          }}
                          id="outlined-multiline-static"
                          label="Special Test"
                          multiline
                          rows={3}
                          // value={specialTests}
                          // onChange={(e) => setSpecialTests(e.target.value)}
                          placeholder="Enter special test details..."
                          slotProps={{
                            inputLabel: { shrink: detailedPrescription?.specialTests ? true : false },
                          }}
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  defaultExpanded
                  variant="outlined"
                  sx={{ width: "100%", maxWidth: "100%" }}
                >
                  <AccordionSummary
                    expandIcon={<GridExpandMoreIcon />}
                    aria-controls="panel3-content"
                    id="panel3-header"
                  >
                    <Typography component="span">
                      Prescribed Treatment
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ padding: "8px", position: "relative" }}
                  >
                    <Grid2
                      container
                      spacing={3}
                      size={12}
                      sx={{ justifyContent: "center", paddingX: "10px" }}
                    >
                      <Grid2 size={12}>
                        <NotesTextField
                          value={detailedPrescription?.treatmentPlan || ""}
                          onChange={(newValue) => {
                            setDetailedPrescription((prev) => ({
                              ...prev,
                              treatmentPlan: newValue,
                            }));
                            handlePrescriptionDetailsChange({ treatmentPlan: newValue });
                          }}
                          id="outlined-multiline-static"
                          label="Treatment Plan"
                          multiline
                          rows={5}
                          // value={treatmentPlan}
                          // onChange={(e) => setTreatmentPlan(e.target.value)}
                          placeholder="Enter treatment plan details..."
                          slotProps={{
                            inputLabel: {
                              shrink: detailedPrescription?.treatmentPlan ? true : false,
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <GenericTextField
                          id="outlined-multiline-static"
                          label="Suggested no. of sessions"
                          type="number"
                          value={detailedPrescription?.suggestedSessions || ""}
                          onChange={(e) => {
                            setDetailedPrescription((prev) => ({
                              ...prev,
                              suggestedSessions: e.target.value,
                            }));
                            handlePrescriptionDetailsChange({ suggestedSessions: e.target.value });
                          }}
                          placeholder="Enter number of sessions..."
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <NotesTextField
                          value={detailedPrescription?.shortTermTreatmentGoals || ""}
                          onChange={(newValue) => {
                            setDetailedPrescription((prev) => ({
                              ...prev,
                              shortTermTreatmentGoals: newValue,
                            }));
                            handlePrescriptionDetailsChange({ shortTermTreatmentGoals: newValue });
                          }}
                          id="outlined-multiline-static"
                          label="Short term treatment goals"
                          multiline
                          rows={4}
                          // value={shortTermTreatmentGoals}
                          // onChange={(e) =>
                          //   setShortTermTreatmentGoals(e.target.value)
                          // }
                          placeholder="Enter short term goals..."
                          slotProps={{
                            inputLabel: {
                              shrink: detailedPrescription?.shortTermTreatmentGoals ? true : false,
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <FormControl>
                          <FormLabel component="legend">
                            Treatment Methods
                          </FormLabel>
                          <FormGroup row>
                            {detailedPrescription?.treatmentMethods?.map((treatment, index) => (
                              <FormControlLabel
                                key={index}
                                control={
                                  <Checkbox
                                    checked={treatment.selected}
                                    onChange={(e) => {
                                      const newConditions = [
                                        ...detailedPrescription.treatmentMethods,
                                      ];
                                      newConditions[index].selected =
                                        e.target.checked;
                                      setDetailedPrescription((prev) => ({
                                        ...prev,
                                        treatmentMethods: newConditions,
                                      }));
                                      handleOthersCheck(newConditions);
                                      handlePrescriptionDetailsChange({ treatmentMethods: newConditions });
                                    }}
                                  />
                                }
                                label={treatment.displayValue}
                              />
                            ))}
                          </FormGroup>
                          {othersChecked && (
                            <TextField
                              id="outlined-multiline-static"
                              label="Specify Other"
                              value={
                                detailedPrescription?.treatmentMethods[
                                  detailedPrescription?.treatmentMethods.findIndex(
                                    (treatment) => treatment.value === "Others",
                                  )
                                ]?.additionalText || ""
                              }
                              onChange={(e) => {
                                const newConditions = [...detailedPrescription.treatmentMethods];
                                newConditions[
                                  detailedPrescription.treatmentMethods.findIndex(
                                    (treatment) => treatment.value === "Others",
                                  )
                                ].additionalText = e.target.value;
                                setDetailedPrescription((prev) => ({
                                  ...prev,
                                  treatmentMethods: newConditions,
                                }));
                                handlePrescriptionDetailsChange({ treatmentMethods: newConditions });
                              }}
                            ></TextField>
                          )}
                        </FormControl>
                      </Grid2>
                      <Grid2 size={12}>
                        <NotesTextField
                          value={detailedPrescription?.longTermTreatmentGoals || ""}
                          onChange={(newValue) => {
                            setDetailedPrescription((prev) => ({
                              ...prev,
                              longTermTreatmentGoals: newValue,
                            }));
                            handlePrescriptionDetailsChange({ longTermTreatmentGoals: newValue });
                          }}
                          id="outlined-multiline-static"
                          label="Long term treatment goals"
                          multiline
                          rows={4}
                          // value={longTermTreatmentGoals}
                          // onChange={(e) =>
                          //   setLongTermTreatmentGoals(e.target.value)
                          // }
                          placeholder="Enter long term goals..."
                          slotProps={{
                            inputLabel: {
                              shrink: detailedPrescription?.longTermTreatmentGoals ? true : false,
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        {/* <GenericTextField
                          id="outlined-multiline-static"
                          label="Do's and Dont's"
                          multiline
                          rows={4}
                          value={dosDonts}
                          onChange={(e) => setDosDonts(e.target.value)}
                          placeholder="Enter do's and dont's..."
                          slotProps={{
                            inputLabel: { shrink: dosDonts ? true : false },
                          }}
                        /> */}
                        <Grid2 size={12}>
                          <Box sx={{ position: "relative" }}>
                            <NotesTextField
                              value={detailedPrescription?.dosDonts || ""}
                              tagsForAutocomplete={["physiotherapy-dos", "physiotherapy-donts"]}
                              onChange={(newValue) => {
                                setDetailedPrescription((prev) => ({
                                  ...prev,
                                  dosDonts: newValue,
                                }));
                                handlePrescriptionDetailsChange({ dosDonts: newValue });
                              }}
                              label="Do's and Dont's"
                              disabled={
                                isSaving || prescriptionStatus === "Final"
                              }
                              rows={4}
                              noteStatus={prescriptionStatus || "Draft"}
                            />
                          </Box>
                        </Grid2>
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </Grid2>
            </Card>
          </Grid2>
        </Grid2>
      </Grid2>
    </Box>
  );
}

export default PhysiotherapyPrescriptionForm;
